import type {
  AttachmentDto,
  AvailableLawyerDto,
  AnswerConversationBody,
  ChangeLawyerBody,
  CloseConversationBody,
  ConversationDto,
  CaseDetailDto,
  CaseSummaryDto,
  CaseDraftCommentBody,
  CreateRefundRequestBody,
  RefundRequestDto,
  PaymentDto,
  PaymentResultDto,
  SubscriptionDto,
  TariffDto,
  ConversationFeedbackBody,
  CreateConversationDto,
  CreateLawyerChangeRequestBody,
  LawyerChangeRequestDto,
  LawyerSummaryDto,
  MessageDto,
  PaginatedResponse,
  SendMessageDto,
  SendMessageResult,
  RequestDocumentsBody,
} from '@lex/contracts';
import type { ApiClient } from './client';

export function createChatApi(client: ApiClient) {
  return {
    listConversations(params?: {
      status?: string;
      scope?: 'mine' | 'queue' | 'closed' | 'all';
      search?: string;
      page?: number;
    }): Promise<PaginatedResponse<ConversationDto>> {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.scope) qs.set('scope', params.scope);
      if (params?.search) qs.set('search', params.search);
      if (params?.page) qs.set('page', String(params.page));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return client.get(`/api/v1/conversations${suffix}`);
    },
    getConversation(id: string): Promise<ConversationDto> {
      return client.get(`/api/v1/conversations/${id}`);
    },
    createConversation(body: CreateConversationDto): Promise<ConversationDto> {
      return client.post('/api/v1/conversations', body);
    },
    listMessages(conversationId: string, before?: string): Promise<PaginatedResponse<MessageDto>> {
      const suffix = before ? `?before=${encodeURIComponent(before)}` : '';
      return client.get(`/api/v1/conversations/${conversationId}/messages${suffix}`);
    },
    sendMessage(conversationId: string, body: SendMessageDto): Promise<SendMessageResult> {
      return client.post(`/api/v1/conversations/${conversationId}/messages`, body);
    },
    listAttachments(conversationId: string): Promise<AttachmentDto[]> {
      return client.get(`/api/v1/conversations/${conversationId}/attachments`);
    },
    deleteAttachment(conversationId: string, attachmentId: string): Promise<{ ok: true }> {
      return client.delete(`/api/v1/conversations/${conversationId}/attachments/${attachmentId}`);
    },
    claimConversation(conversationId: string): Promise<unknown> {
      return client.post(`/api/v1/conversations/${conversationId}/claim`, {});
    },
    markRead(conversationId: string): Promise<{ ok: true }> {
      return client.post(`/api/v1/conversations/${conversationId}/read`);
    },
    closeConversation(
      conversationId: string,
      body: CloseConversationBody = {}
    ): Promise<ConversationDto> {
      return client.patch(`/api/v1/conversations/${conversationId}`, body);
    },
    answerConversation(
      conversationId: string,
      body: AnswerConversationBody
    ): Promise<ConversationDto> {
      return client.post(`/api/v1/conversations/${conversationId}/answer`, body);
    },
    requestDocuments(
      conversationId: string,
      body: RequestDocumentsBody = {}
    ): Promise<ConversationDto> {
      return client.post(`/api/v1/conversations/${conversationId}/request-documents`, body);
    },
    rateConversation(
      conversationId: string,
      body: ConversationFeedbackBody
    ): Promise<ConversationDto> {
      return client.post(`/api/v1/conversations/${conversationId}/feedback`, body);
    },
    reopenConversation(conversationId: string): Promise<ConversationDto> {
      return client.post(`/api/v1/conversations/${conversationId}/reopen`, {});
    },
    currentLawyer(): Promise<LawyerSummaryDto | null> {
      return client.get('/api/v1/me/lawyer');
    },
    availableLawyers(): Promise<AvailableLawyerDto[]> {
      return client.get('/api/v1/me/lawyer/available');
    },
    changeLawyer(body: ChangeLawyerBody): Promise<unknown> {
      return client.post('/api/v1/me/lawyer/change', body);
    },
    myLawyerChangeRequest(): Promise<LawyerChangeRequestDto | null> {
      return client.get('/api/v1/me/lawyer/change-request');
    },
    requestLawyerChange(body: CreateLawyerChangeRequestBody): Promise<LawyerChangeRequestDto> {
      return client.post('/api/v1/me/lawyer/change-request', body);
    },
    cancelLawyerChangeRequest(id: string): Promise<LawyerChangeRequestDto> {
      return client.post(`/api/v1/me/lawyer/change-request/${id}/cancel`, {});
    },
    myCases(): Promise<CaseSummaryDto[]> {
      return client.get('/api/v1/me/cases');
    },
    myCase(id: string): Promise<CaseDetailDto> {
      return client.get(`/api/v1/me/cases/${id}`);
    },
    commentCaseDraft(id: string, body: CaseDraftCommentBody): Promise<CaseDetailDto> {
      return client.post(`/api/v1/me/cases/${id}/comment`, body);
    },
    subscription(): Promise<SubscriptionDto | null> {
      return client.get('/api/v1/me/billing/subscription');
    },
    subscriptionHistory(): Promise<SubscriptionDto[]> {
      return client.get('/api/v1/me/billing/subscriptions');
    },
    plans(): Promise<TariffDto[]> {
      return client.get('/api/v1/me/billing/plans');
    },
    payments(): Promise<PaymentDto[]> {
      return client.get('/api/v1/me/billing/payments');
    },
    createPayment(planKey: string): Promise<PaymentDto> {
      return client.post('/api/v1/me/billing/payments', { planKey });
    },
    confirmPayment(id: string): Promise<PaymentResultDto> {
      return client.post(`/api/v1/me/billing/payments/${id}/confirm`, {});
    },
    setAutoRenew(enabled: boolean): Promise<SubscriptionDto> {
      return client.patch('/api/v1/me/billing/auto-renew', { enabled });
    },
    refunds(): Promise<RefundRequestDto[]> {
      return client.get('/api/v1/me/billing/refunds');
    },
    requestRefund(body: CreateRefundRequestBody): Promise<RefundRequestDto> {
      return client.post('/api/v1/me/billing/refunds', body);
    },
  };
}
